"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.makeExperimentFlags = exports.experimentStates = void 0;

var _get2 = _interopRequireDefault(require("lodash/get"));

var _environment = require("./environment");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/**
 * Server-side experiment implementation
 *
 * For use when client-side segment tracking not available.
 * (Most code copied from client-side version but with tracking stripped out)
 *
 * NOTE: Must explicitly implement tracking separately in respective web app
 */

/**
 * Enum for test values.
 *
 * @readonly
 * @enum {string}
 */
const experimentStates = {
  NOT_IN_TEST: 'NOT_IN_TEST',
  CONTROL: 'TEST-0',
  TEST_0: 'TEST-0',
  TEST_1: 'TEST-1',
  TEST_2: 'TEST-2',
  TEST_3: 'TEST-3',
  TEST_4: 'TEST-4',
  TEST_5: 'TEST-5',
  TEST_6: 'TEST-6',
  TEST_7: 'TEST-7',
  TEST_8: 'TEST-8',
  TEST_9: 'TEST-9'
};
exports.experimentStates = experimentStates;

/**
 * makes the environment-based defaults for a flag
 */
const createExperimentFlags = (development = experimentStates.NOT_IN_TEST, staging = experimentStates.NOT_IN_TEST, production = experimentStates.NOT_IN_TEST) => {
  return {
    development,
    staging,
    production
  };
};

const getOverrideVariation = (experimentName, cookies) => {
  const experimentCookie = cookies.zola_flag_overrides;
  const overrides = JSON.parse(experimentCookie || '{}') || {};
  return overrides[experimentName];
};
/**
 * Given an list of fallback variations for the flag and tracking callback function, provide functions for
 * determining if a user is an experiment, in an A group, or in a B group.
 *
 * For simple A/B testing, where a user who is not in the test, is treated the
 * same as the control, you will only need to use `isUnderTest`.
 *
 * @param fallbackVariations
 * @example - A/B testing with the control and not-in-test treated the same
 *
 * // in your web-app-specific helper, define the flags.
 * // note, that if the default fallback for the flags are all NOT_IN_TEST, you can actually ommit
 * // the FALLBACK_VARIATIONS
 * // -- web-app/util/experimentFlags.js --
 * const FALLBACK_VARIATIONS = {
 * 'stanford-prison-experiment': experimentFlagsHelper.createExperimentFlags(NOT_IN_TEST, NOT_IN_TEST, NOT_IN_TEST),
 * };
 * export default experimentFlagsHelper.makeExperimentFlags(FALLBACK_VARIATIONS);
 */


const makeExperimentFlags = fallbackVariations => {
  const getFallbackVariation = flagOrExperimentName => {
    if (fallbackVariations[flagOrExperimentName]) {
      return fallbackVariations[flagOrExperimentName][(0, _environment.getEnvironmentExperimentFlag)()];
    }

    return experimentStates.NOT_IN_TEST;
  };
  /**
   * Returns the variation for the experiment / user / environment
   *
   * @param {string} experimentName
   * @param {object} userContext
   * @param {object} cookies - optional, used for overrides
   *
   * @example
   * getExperimentVariation('WEAR_A_MASK', userContext) => 'NOT_IN_TEST' // the user is not participating in the test
   * getExperimentVariation('WEAR_A_MASK', userContext) => 'TEST-0'      // the user is in the control group
   * getExperimentVariation('WEAR_A_MASK', userContext) => 'TEST-1'      // the user is in the first test group
   * getExperimentVariation('WEAR_A_MASK', userContext) => 'TEST-7'      // the user is in the seventh test group
   *
   * returns 'NOT_IN_TEST' if the user is not part of the experiment
   * returns 'TEST-0' if the user is in the control group
   * returns 'TEST-<N>' if the user is one of the test groups
   */


  const getExperimentVariation = (experimentName, userContext, cookies) => {
    if ((cookies === null || cookies === void 0 ? void 0 : cookies.automatedTest) === 'true') {
      return experimentStates.NOT_IN_TEST;
    }

    const fallbackValue = getFallbackVariation(experimentName);
    const experimentState = (0, _get2.default)(userContext, `user_experiments.${experimentName}`, {});
    let variationKey = experimentState.variation_key;
    if (!variationKey) variationKey = fallbackValue;
    const noStateError = new Error('No experiment state found, please ensure new experiments have a fallback config.');
    if (!variationKey) throw noStateError; // Return override only on dev & stage

    const isProduction = (0, _environment.getEnvironmentExperimentFlag)() === 'production';
    const overrideVariation = getOverrideVariation(experimentName, cookies || {});

    if (!isProduction && overrideVariation) {
      return overrideVariation;
    }

    return variationKey;
  };

  return {
    getExperimentVariation
  };
};

exports.makeExperimentFlags = makeExperimentFlags;
var _default = {
  experimentStates,
  makeExperimentFlags,
  createExperimentFlags
};
exports.default = _default;